// @ts-nocheck
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Package } from '@way/theme-host/shared/types';
import {
  SharedPackageCardComponent,
  PackageCardOption,
} from '../package-card/package-card.component';
import { LocationService } from '@way/theme-host/services/location.service';
import { OrderCreationService } from '@way/theme-host/services/order-creation.service';
import { NotificationService } from '../../../../app/services/notification.service'; // NotificationService;
import { isLicensePlateNeeded } from './license-plate-required.util';
import { mapPackageModalLocations } from './map-package-modal-locations.util';
import { formatStrikeoffPriceLabel } from '@way/theme-host/shared/utils/strikeoff-price.util';
import { environment } from '@way/theme-host/env/environment';

interface WashTier {
  id: string;
  name: string;
  features: string[];
  options: PackageCardOption[];
}

export interface SelectedPackage {
  packageId: string;
  packageName: string;
  optionId: string;
  optionType: 'single' | 'monthly';
  price: string;
}

/**
 * Modal component for selecting shared car wash packages, collecting basic customer details, and initiating an order flow. Presents package tiers and options, allows selection of a package option, transitions to a customer details step, loads locations, and emits the selected package or a close event.
 * @component
 * @selector app-shared-package-selection-modal
 * @example
 *   <app-shared-package-selection-modal
 *     [isVisible]="true"
 *     [packages]="availablePackages"
 *     (close)="handleClose()"
 *     (packageSelected)="handlePackageSelected($event)">
 *   </app-shared-package-selection-modal>
 *
 * Key behaviors:
 * - Renders package tiers and options derived from raw package data.
 * - Manages a two-step flow: 'package-selection' and 'customer-details'.
 * - Loads branch locations (admin/preview mode) and validates customer input before attempting order submission.
 * - Emits selection via packageSelected and notifies parent when modal is closed via close.
 *
 * Lifecycle hooks:
 * - ngOnInit: Initializes wash tiers and default selections when packages are present.
 * - ngOnChanges: Resets step state when modal becomes visible and updates tiers/selections when packages change.
 *
 * Key methods:
 * - transformPackagesToWashTiers(): Converts incoming packages[] into washTiers structure and filters out inactive packages or options without valid pricing.
 * - setDefaultSelections(): Picks the first tier and option as default selections when available.
 * - onTierOptionSelected(tierId: string, optionId: string): Updates selected tier and option.
 * - onContinue(): Advances the flow — emits packageSelected and transitions to customer-details, or validates customer form and triggers submitOrder().
 * - loadLocations(): Loads available locations using LocationService in admin/preview mode; sets locations and locationsError accordingly.
 * - onBack(): Returns to the package selection step from customer-details.
 * - submitOrder(): Validates form, prepares payload and (in non-preview mode) would create an order; in preview mode shows informational message and closes modal.
 * - isAdminPreviewMode(): Detects admin/preview mode via localStorage.
 * - isWebsitePreviewMode(): Detects website preview mode via URL path to prevent API calls during preview.
 * - trackByTier(_: number, tier: WashTier): TrackBy function for rendering tiers by id.
 *
 * @input boolean isVisible - Controls modal visibility; when true the component resets to package-selection step and clears customer form.
 * @input Package[] packages - Array of raw package objects provided to the component; used to build wash tiers and options (filters inactive or improperly priced packages).
 * @output EventEmitter<void> close - Emitted when the user closes the modal or after preview-order handling; parent should hide the modal.
 * @output EventEmitter<SelectedPackage> packageSelected - Emitted when a package option is selected from the package-selection step; payload includes packageId, packageName, optionId, optionType and price.
 */
@Component({
  selector: 'app-shared-package-selection-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedPackageCardComponent],
  providers: [NotificationService],
  templateUrl: './package-selection-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./package-selection-modal.component.scss'],
})
export class SharedPackageSelectionModalComponent implements OnInit, OnChanges {
  @Input() isVisible = false;
  @Input() packages: Package[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() packageSelected = new EventEmitter<SelectedPackage>();

  washTiers: WashTier[] = [];
  selectedTierId = '';
  selectedOptionId = '';
  isLoading = false;

  // Step management
  currentStep: 'package-selection' | 'customer-details' = 'package-selection';

  // Customer details form
  customerForm = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    licensePlate: '',
    location: '',
  };

  // Locations
  locations: any[] = [];
  isLoadingLocations = false;
  locationsError = '';

  // Submit state
  isSubmitting = false;

  constructor(
    private logger: LoggerService,
    private http: HttpClient,
    private locationService: LocationService,
    private orderCreationService: OrderCreationService,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    if (this.packages && this.packages.length > 0) {
      this.transformPackagesToWashTiers();
      this.setDefaultSelections();
    }
  }

  /**
   * Handle Angular SimpleChanges lifecycle updates; when isVisible changes from false to true reset modal state and initialize package selections.
   * @example
   * gOnChanges({ isVisible: { previousValue: false, currentValue: true } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges object containing property change information for the component.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isVisible'] &&
      changes['isVisible'].previousValue === false &&
      changes['isVisible'].currentValue === true
    ) {
      // Reset to package selection step when modal opens
      this.currentStep = 'package-selection';
      // Reset customer form
      this.customerForm = {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        licensePlate: '',
        location: '',
      };
      // Reset locations
      this.locations = [];
      this.isLoadingLocations = false;
      this.locationsError = '';
    }

    if (this.packages && this.packages.length > 0) {
      this.transformPackagesToWashTiers();
      this.setDefaultSelections();
    }
  }

  /**
   * Transform the component's packages array into wash tier objects and assign them to this.washTiers.
   * @example
   * transformPackagesToWashTiers()
   * undefined
   * @param {Array<Object>} packages - The component's packages array (this.packages) used to build wash tiers.
   * @returns {void} Does not return a value; updates this.washTiers with the computed WashTier objects.
   **/
  private transformPackagesToWashTiers(): void {
    if (!this.packages || !Array.isArray(this.packages)) {
      this.washTiers = [];
      return;
    }

    const washTiers: WashTier[] = [];

    this.packages.forEach(packageData => {
      // Only show active packages
      if (!packageData.package_status) {
        return;
      }

      const washTier: WashTier = {
        id: packageData.id || '',
        name: packageData.package_name || '',
        features: packageData.features || [],
        options: [],
      };

      // Add single wash option if available
      if (
        packageData.single_list_price &&
        parseFloat(packageData.single_list_price) > 0 &&
        packageData.wash_types &&
        packageData.wash_types.includes('single_wash')
      ) {
        const singleOption: PackageCardOption = {
          id: `${packageData.id || ''}-single`,
          label: 'Single Wash',
          description: `${(packageData.features || []).length} benefits`,
          priceLabel: `$${parseFloat(packageData.single_list_price || '0').toFixed(2)}`,
          originalPriceLabel: formatStrikeoffPriceLabel(packageData.single_strikeoff_price),
          type: 'single',
          family_plan: packageData.family_plan,
        };
        washTier.options.push(singleOption);
      }

      // Add monthly option if available
      if (
        packageData.monthly_list_price &&
        parseFloat(packageData.monthly_list_price) > 0 &&
        packageData.wash_types &&
        packageData.wash_types.includes('monthly_membership')
      ) {
        // Determine wash count description
        let washCountDescription = 'Unlimited washes';
        if (packageData.wash_count_period === 'unlimited') {
          washCountDescription = 'Unlimited washes';
        } else if (
          packageData.wash_count_period === 'per_month' &&
          packageData.wash_count_frequency
        ) {
          washCountDescription = `${packageData.wash_count_frequency} washes`;
        }

        const monthlyOption: PackageCardOption = {
          id: `${packageData.id || ''}-monthly`,
          label: 'Monthly',
          description: `${(packageData.features || []).length} benefits • ${washCountDescription}`,
          priceLabel: `$${parseFloat(packageData.monthly_list_price || '0').toFixed(2)}/mo`,
          originalPriceLabel: formatStrikeoffPriceLabel(packageData.monthly_strikeoff_price),
          type: 'monthly',
          note: `The car wash membership will renew automatically. The customer can change the membership status any time from their membership settings in the Way App.`,
          family_plan: packageData.family_plan,
        };
        washTier.options.push(monthlyOption);
      }

      // Only add tiers that have at least one option
      if (washTier.options.length > 0) {
        washTiers.push(washTier);
      }
    });

    this.washTiers = washTiers;
  }

  private setDefaultSelections(): void {
    if (this.washTiers.length > 0) {
      const [firstTier] = this.washTiers;
      this.selectedTierId = firstTier.id;
      if (firstTier.options.length > 0) {
        this.selectedOptionId = firstTier.options[0].id;
      }
    }
  }

  onTierOptionSelected(tierId: string, optionId: string): void {
    this.selectedTierId = tierId;
    this.selectedOptionId = optionId;
  }

  onClose(): void {
    this.close.emit();
  }

  /**
   * Advance the modal workflow: when on package selection, validate selection, emit the chosen package, and transition to customer details; when on customer details, validate the form and submit the order.
   * @example
   * nContinue()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Does not return a value.
   **/
  onContinue(): void {
    if (this.currentStep === 'package-selection') {
      if (!this.selectedTierId || !this.selectedOptionId) {
        return;
      }

      // Find the selected tier and option
      const selectedTier = this.washTiers.find(tier => tier.id === this.selectedTierId);
      if (!selectedTier) return;

      const selectedOption = selectedTier.options.find(opt => opt.id === this.selectedOptionId);
      if (!selectedOption) return;

      // Find the original package
      const selectedPackage = this.packages.find(pkg => pkg.id === this.selectedTierId);
      if (!selectedPackage) return;

      // Determine option type
      const optionType = selectedOption.id.includes('-monthly') ? 'monthly' : 'single';

      const result: SelectedPackage = {
        packageId: selectedPackage.id,
        packageName: selectedPackage.package_name,
        optionId: selectedOption.id,
        optionType: optionType,
        price: selectedOption.priceLabel,
      };

      // Emit the selected package
      this.packageSelected.emit(result);

      // Move to customer details step and load locations
      try {
        this.currentStep = 'customer-details';
        setTimeout(() => {
          this.loadLocations();
        }, 100);
      } catch (error) {
        this.logger.error('Error transitioning to customer details step:', error);
      }
    } else if (this.currentStep === 'customer-details') {
      if (!this.isCustomerDetailsComplete) {
        return;
      }

      this.submitOrder();
    }
  }

  get isLicensePlateRequired(): boolean {
    const selectedPackage = this.packages.find(pkg => pkg.id === this.selectedTierId);
    return isLicensePlateNeeded(selectedPackage?.license_plate_needed);
  }

  get isCustomerDetailsComplete(): boolean {
    if (
      !this.customerForm.firstName ||
      !this.customerForm.lastName ||
      !this.customerForm.phone ||
      !this.customerForm.location
    ) {
      return false;
    }
    if (this.isLicensePlateRequired && !this.customerForm.licensePlate?.trim()) {
      return false;
    }
    return true;
  }

  /**
   * Check if we're in admin/preview mode (website builder)
   */
  private isAdminPreviewMode(): boolean {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        return Boolean(parsedData?.token);
      }
    } catch (error) {
      // If parsing fails, assume not admin mode
    }
    return false;
  }

  /**
   * Check if we're in website preview mode (URL contains /preview)
   */
  private isWebsitePreviewMode(): boolean {
    try {
      return window.location.pathname.includes('/preview');
    } catch (error) {
      return false;
    }
  }

  /**
   * Loads location data into the component when not already loaded. If the component is in admin/preview mode, this method requests locations from the LocationService, filters for active locations, maps them to a simplified shape (id, name, address, fullData), assigns the first location to the customer form if none is selected, and updates loading and error state accordingly. If not in admin mode it clears locations and sets an authentication-required error. Handles network errors by logging and setting a retryable error message.
   * @example
   * this.oadLocations()
   * // undefined
   * @returns {void} No return value.
   */
  loadLocations(): void {
    if (this.locations.length > 0) {
      return;
    }

    if (this.isLoadingLocations) {
      return;
    }

    this.isLoadingLocations = true;
    this.locationsError = '';

    if (this.isAdminPreviewMode()) {
      this.locationService.getLocations().subscribe({
        next: (response: any) => this.applyLoadedLocations(response),
        error: (error: any) => this.handleLocationsError(error, 'admin'),
      });
      return;
    }

    this.http.get(`${environment.apiUrl}/chat/locations`).subscribe({
      next: (response: any) => this.applyLoadedLocations(response),
      error: (error: any) => this.handleLocationsError(error, 'published'),
    });
  }

  private applyLoadedLocations(response: any): void {
    this.isLoadingLocations = false;
    this.locations = mapPackageModalLocations(response);
    if (this.locations.length === 0) {
      this.locationsError = 'No locations found.';
      return;
    }
    this.locationsError = '';
    if (!this.customerForm.location && this.locations[0]?.id) {
      this.customerForm.location = this.locations[0].id;
    }
  }

  private handleLocationsError(error: any, source: 'admin' | 'published'): void {
    this.logger.error(`Error loading locations (${source}):`, error);
    this.isLoadingLocations = false;
    this.locations = [];
    this.locationsError = 'No locations found. Please try again later.';
  }

  onBack(): void {
    if (this.currentStep === 'customer-details') {
      this.currentStep = 'package-selection';
    }
  }

  trackByTier(_: number, tier: WashTier): string {
    return tier.id;
  }

  /**
   * Submit order to API
   */
  submitOrder(): void {
    if (this.isSubmitting) {
      return;
    }

    // Check if we're in website preview mode - don't make API calls
    if (this.isWebsitePreviewMode()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Preview Mode',
        detail: 'This feature is not available in website preview.',
        life: 5000,
      });
      return;
    }

    if (!this.isCustomerDetailsComplete) {
      return;
    }

    // Get selected package and option
    const selectedTier = this.washTiers.find(tier => tier.id === this.selectedTierId);
    if (!selectedTier) {
      this.logger.error('No selected tier found');
      return;
    }

    if (!selectedTier.options.find(opt => opt.id === this.selectedOptionId)) {
      this.logger.error('No selected option found');
      return;
    }

    this.isSubmitting = true;

    // For preview components, show a message since order creation requires proper customer/vehicle IDs
    // that don't exist in preview mode. The OrderCreationService requires customer_id and vehicle_id.
    this.messageService.add({
      severity: 'info',
      summary: 'Preview Mode',
      detail:
        'Order creation is not available in preview. This feature works on the published website.',
      life: 5000,
    });
    this.isSubmitting = false;
    this.close.emit();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
